import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Home, DollarSign, MapPin, Users } from 'lucide-react';
type FAQItem = {
  question: string;
  answer: string;
};
type FAQCategory = {
  name: string;
  icon: React.ElementType;
  faqs: FAQItem[];
};
const faqCategories: FAQCategory[] = [{
  name: 'Buying a Home',
  icon: Home,
  faqs: [{
    question: 'What is the home buying process in Las Vegas?',
    answer: "The home buying process typically starts with getting pre-approved for a mortgage, then searching for homes that fit your criteria. Once you find the right property, you'll make an offer, negotiate terms, complete inspections, and close escrow. In Nevada, the process usually takes 30–45 days from accepted offer to closing. Jenny will guide you through every step to ensure a smooth experience."
  }, {
    question: 'How much do I need for a down payment?',
    answer: 'Down payment requirements vary by loan type. Conventional loans typically require 5–20% down, FHA loans require as little as 3.5%, and VA loans (for eligible veterans) may require zero down payment. In the Las Vegas market, the average home price means your down payment could range from $15,000 to over $100,000 depending on the property and loan type. Jenny can connect you with trusted local lenders to explore your options.'
  }, {
    question: 'What are closing costs and how much should I expect?',
    answer: "Closing costs in Nevada typically range from 2–5% of the purchase price. These include lender fees, title insurance, escrow fees, appraisal costs, and prepaid items like property taxes and homeowner's insurance. On a $600,000 home, you might expect $12,000–$30,000 in closing costs. Some of these can be negotiated with the seller."
  }, {
    question: 'Should I get a home inspection?',
    answer: 'Absolutely. A home inspection is one of the most important steps in the buying process. In Las Vegas, inspectors will check for common desert-climate issues like stucco cracks, HVAC efficiency (critical in our heat), roof condition, plumbing, and potential water damage. The inspection typically costs $300–$500 and can save you thousands by identifying issues before you close.'
  }, {
    question: 'What should I look for when buying in Las Vegas?',
    answer: "Key factors include the community and HOA (many Las Vegas neighborhoods have HOAs with varying fees and amenities), proximity to schools and shopping, lot size, energy efficiency (important given our climate), and future development plans in the area. Jenny's deep local knowledge helps you evaluate neighborhoods beyond just the property itself."
  }]
}, {
  name: 'Selling a Home',
  icon: DollarSign,
  faqs: [{
    question: 'How do I prepare my home before I sell it?',
    answer: 'When preparing to sell your home, the first thing to do is make it presentable. Start with decluttering and deep cleaning every room. Consider fresh paint in neutral tones, updating light fixtures, and enhancing curb appeal — especially important in Las Vegas where outdoor spaces are a major selling point. Stage key rooms to help buyers envision themselves living there. Jenny will provide a personalized checklist based on your specific property and market conditions.'
  }, {
    question: 'How long will it take to sell my home?',
    answer: 'On average, a house takes between 55 and 70 days to sell. That includes approximately 25 days on the market to receive and accept an offer, plus 30–45 days to close escrow. However, well-priced homes in desirable Las Vegas communities like Summerlin, Henderson, and Southern Highlands often sell faster — sometimes within days. Market conditions, pricing strategy, and presentation all play a role.'
  }, {
    question: 'How do I determine the right listing price?',
    answer: 'Pricing your home correctly from the start is crucial. Jenny conducts a thorough Comparative Market Analysis (CMA) examining recent sales of similar homes in your area, current market conditions, and unique features of your property. Overpricing can lead to extended time on market, while underpricing leaves money on the table. The goal is to attract maximum interest and competitive offers.'
  }, {
    question: 'What are the costs of selling a home in Nevada?',
    answer: "Typical seller costs include real estate commission (typically 5–6% of sale price), title insurance, escrow fees, transfer taxes, and any negotiated repairs or credits. In Nevada, there's also a Real Property Transfer Tax. On a $600,000 home, total selling costs might range from $35,000–$45,000. Jenny will provide a detailed net sheet so you know exactly what to expect."
  }, {
    question: 'Do I need to make repairs before selling?',
    answer: 'Not necessarily, but strategic repairs and updates can significantly increase your sale price and reduce time on market. Focus on high-impact, low-cost improvements: fresh paint, updated hardware, landscaping, and fixing any obvious maintenance issues. In Las Vegas, ensuring your AC system is in good working order is especially important to buyers. Jenny will advise which improvements offer the best return on investment.'
  }]
}, {
  name: 'Las Vegas Market',
  icon: MapPin,
  faqs: [{
    question: 'Is Las Vegas a good place to invest in real estate?',
    answer: 'Las Vegas has consistently been one of the strongest real estate markets in the country. With no state income tax, a growing population, major corporate relocations, and a diversifying economy beyond tourism, the city offers excellent investment potential. Property values have shown strong appreciation, and the rental market is robust due to continued population growth and demand.'
  }, {
    question: 'What are the best neighborhoods to buy in Las Vegas?',
    answer: 'The best neighborhood depends on your lifestyle and budget. Summerlin offers master-planned luxury with Red Rock Canyon views. Southern Highlands features exclusive gated estates. Henderson provides family-friendly upscale living in areas like Green Valley and Anthem. MacDonald Highlands is ultra-luxury with panoramic city views. Spring Valley offers central convenience, while Southwest Las Vegas has exciting new developments. Jenny specializes in all these areas and will match you with the perfect community.'
  }, {
    question: 'What is the average home price in Las Vegas?',
    answer: 'As of 2024, the median home price in the Las Vegas metro area is approximately $425,000–$450,000. However, prices vary significantly by area: North Las Vegas offers entry points around $350,000, while luxury communities like The Ridges in Summerlin or MacDonald Highlands can exceed several million. Henderson and Summerlin typically range from $500,000–$800,000 for quality homes.'
  }, {
    question: 'How are property taxes in Nevada?',
    answer: "Nevada has some of the most favorable property tax rates in the nation. The effective property tax rate is approximately 0.53%, well below the national average of 1.07%. On a $600,000 home, you'd pay roughly $3,180 annually in property taxes. Combined with no state income tax, Nevada offers significant financial advantages for homeowners."
  }, {
    question: 'What should I know about HOAs in Las Vegas?',
    answer: "Many Las Vegas communities, especially master-planned ones like Summerlin and Southern Highlands, have Homeowners Associations. HOA fees typically range from $25–$300+ per month depending on the community and amenities (pools, parks, security, landscaping). HOAs maintain community standards and property values. Jenny will help you understand the specific HOA rules, fees, and financial health of any community you're considering."
  }]
}, {
  name: 'Working with Jenny',
  icon: Users,
  faqs: [{
    question: 'Why should I work with a local Las Vegas realtor?',
    answer: "A local realtor like Jenny brings invaluable knowledge that out-of-area agents simply can't match. She understands micro-market trends in each neighborhood, knows which communities are appreciating fastest, has relationships with local lenders and inspectors, and can provide insights about upcoming developments that could affect property values. Her dedication to the Las Vegas market means you get expert guidance tailored to this unique city."
  }, {
    question: 'What areas does Jenny specialize in?',
    answer: "Jenny specializes in Las Vegas, Henderson, North Las Vegas, Summerlin, Southern Highlands, Spring Valley, Southwest Las Vegas, Green Valley, and all surrounding communities in Clark County. Whether you're looking for a luxury estate, a family home, or an investment property, Jenny has the expertise to help you find exactly what you need."
  }, {
    question: 'How do I get started with Jenny?',
    answer: "Getting started is easy! Simply call Jenny at (858) 397-8218 or use the contact form on this website. She'll schedule a no-obligation consultation to understand your goals, timeline, and preferences. Whether you're buying, selling, or just exploring your options, Jenny is here to provide honest guidance and expert advice every step of the way."
  }, {
    question: 'Does it cost me anything to work with Jenny as a buyer?',
    answer: "In most cases, buyer's agent services are free to the buyer — the seller typically pays the real estate commission. This means you get Jenny's full expertise, negotiation skills, and market knowledge at no direct cost to you. She'll represent your interests throughout the entire transaction and ensure you get the best possible deal."
  }]
}];
function AccordionItem({
  item,
  isOpen,
  onToggle
}: {
  item: FAQItem;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <div className="border-b border-warmGray-dark" data-id="element-89">
      <button onClick={onToggle} className="w-full flex items-center justify-between py-6 text-left group" data-id="element-90">
        <span className={`text-lg font-serif pr-8 transition-colors duration-300 ${isOpen ? 'text-gold' : 'text-charcoal group-hover:text-gold'}`} data-id="element-91">
          {item.question}
        </span>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.3
      }} className="flex-shrink-0" data-id="element-92">
          <ChevronDown className={`w-5 h-5 transition-colors duration-300 ${isOpen ? 'text-gold' : 'text-charcoal-light'}`} data-id="element-93" />
        </motion.div>
      </button>
      <AnimatePresence data-id="element-94">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-95">
            <p className="pb-6 text-charcoal-light font-light leading-relaxed pr-12" data-id="element-96">
              {item.answer}
            </p>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function FAQ() {
  const [activeCategory, setActiveCategory] = useState(0);
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
  const toggleItem = (key: string) => {
    setOpenItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  return <section id="faq" className="py-24 md:py-32 bg-white px-6" data-id="element-97">
      <div className="max-w-5xl mx-auto" data-id="element-98">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-99">
          <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-100">
            Have Questions?
          </span>
          <h2 className="text-4xl md:text-5xl font-serif text-charcoal mb-6" data-id="element-101">
            Frequently Asked <span className="italic" data-id="element-102">Questions</span>
          </h2>
          <p className="text-lg text-charcoal-light font-light max-w-2xl mx-auto" data-id="element-103">
            Everything you need to know about buying, selling, and investing in
            Las Vegas real estate.
          </p>
        </motion.div>

        {/* Category Tabs */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.1
      }} className="flex flex-wrap justify-center gap-3 mb-14" data-id="element-104">
          {faqCategories.map((category, index) => {
          const Icon = category.icon;
          return <button key={category.name} onClick={() => setActiveCategory(index)} className={`flex items-center gap-2 px-6 py-3 text-sm uppercase tracking-wider transition-all duration-300 border ${activeCategory === index ? 'bg-gold text-white border-gold' : 'bg-transparent text-charcoal-light border-warmGray-dark hover:border-gold hover:text-gold'}`} data-id="element-105">
                <Icon className="w-4 h-4" data-id="element-106" />
                {category.name}
              </button>;
        })}
        </motion.div>

        {/* FAQ Accordion */}
        <motion.div key={activeCategory} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} className="border-t border-warmGray-dark" data-id="element-107">
          {faqCategories[activeCategory].faqs.map((faq, index) => {
          const key = `${activeCategory}-${index}`;
          return <AccordionItem key={key} item={faq} isOpen={!!openItems[key]} onToggle={() => toggleItem(key)} data-id="element-108" />;
        })}
        </motion.div>

        {/* CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mt-16 pt-8" data-id="element-109">
          <p className="text-charcoal-light font-light mb-6" data-id="element-110">
            Don't see your question? Jenny is happy to help.
          </p>
          <a href="tel:+18583978218" className="inline-block bg-gold text-white px-10 py-4 uppercase tracking-widest text-sm hover:bg-gold-dark transition-colors duration-300" data-id="element-111">
            Call (858) 397-8218
          </a>
        </motion.div>
      </div>
    </section>;
}